'use client';

import React, { useState, useRef, useEffect } from 'react';
import { SUPPORTED_LANGUAGES, Language } from '@/lib/i18n';

interface LanguageSwitcherProps {
  currentLanguage: Language;
  onLanguageChange: (lang: Language) => void;
  dark?: boolean;
}

export default function LanguageSwitcher({ currentLanguage, onLanguageChange, dark = false }: LanguageSwitcherProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = SUPPORTED_LANGUAGES.find(l => l.code === currentLanguage) ?? SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const textColor = dark ? 'rgba(255,255,255,0.75)' : '#5b6b78';
  const hoverBg = dark ? 'rgba(255,255,255,0.08)' : '#f1f5f8';
  const dropdownBg = dark ? '#1a3d52' : '#fff';
  const dropdownBorder = dark ? 'rgba(255,255,255,0.12)' : '#e4eaf0';
  const activeBg = dark ? 'rgba(242,101,34,0.15)' : '#fde9df';

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 12px',
          borderRadius: 8,
          border: `1px solid ${dark ? 'rgba(255,255,255,0.15)' : '#e4eaf0'}`,
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          color: textColor,
          fontFamily: 'inherit',
          transition: 'background 0.2s, border-color 0.2s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        aria-label="Select language"
        aria-expanded={open}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>{current.flag}</span>
        <span>{current.code.toUpperCase()}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', opacity: 0.6 }}
        >
          <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            background: dropdownBg,
            border: `1px solid ${dropdownBorder}`,
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(16,38,52,0.16)',
            zIndex: 1000,
            minWidth: 160,
            overflow: 'hidden',
            animation: 'fadeInDown 0.15s ease',
          }}
        >
          {SUPPORTED_LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => { onLanguageChange(lang.code); setOpen(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 14px',
                background: lang.code === currentLanguage ? activeBg : 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13.5,
                fontWeight: lang.code === currentLanguage ? 700 : 500,
                color: lang.code === currentLanguage ? '#F26522' : textColor,
                fontFamily: 'inherit',
                textAlign: 'left',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (lang.code !== currentLanguage) e.currentTarget.style.background = hoverBg; }}
              onMouseLeave={e => { if (lang.code !== currentLanguage) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>{lang.flag}</span>
              <span>{lang.nativeName}</span>
              {lang.code === currentLanguage && (
                <span style={{ marginLeft: 'auto', color: '#F26522' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 7l3 3 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
